import {
    FC,
    ReactElement,
    useRef,
    useState,
} from 'react';

import { Button, Picture, SafeHtml } from '../../../../../components';
import { Dialog, IconButton, ModalContent } from '../../../../../compositions';
import { Embed } from '../../../../../entities/Embed/Embed';
import { ImageFormatSize } from '../../../../../entities/Media/Media';
import { Solution } from '../../../../../entities/Solution/Solution';
import useTrans from '../../../../../hooks/useTrans';

import './SolutionEmbedCard.scss';

export interface SolutionEmbedCardProps extends Solution {
    embed: Embed;
    className?: string;
}

const SolutionEmbedCard: FC<SolutionEmbedCardProps> = ({
    title,
    image,
    embed,
    buttonLabel,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const dialogRef = useRef<HTMLDialogElement>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleOpenDialog = async (): Promise<void> => {
        if (!dialogRef.current) return;

        dialogRef.current.showModal();
        setIsOpen(true);
    };

    const handleCloseDialog = async (): Promise<void> => {
        if (!dialogRef.current) return;

        dialogRef.current.close();
        setIsOpen(false);
    };

    const thumbnail = embed.thumbnail || image;

    return (
        <div className={`solution-embed-card ${className}`}>
            <div className="solution-embed-card__image-wrapper">
                {thumbnail ? (
                    <Picture
                        hasScaling
                        src={thumbnail.src}
                        alt={thumbnail.alt}
                        formats={thumbnail.formats}
                        formatOverride={ImageFormatSize.small}
                        className="solution-embed-card__picture"
                        imageClassName="solution-embed-card__image"
                    />
                ) : (
                    <div className="solution-embed-card__picture-placeholder" />
                )}

                <div className="solution-embed-card__play-button-wrapper">
                    <IconButton
                        icon="play"
                        text={trans('compositions.video.button.play')}
                        hideLabel
                        onClick={handleOpenDialog}
                        className="solution-embed-card__play-button"
                        iconClassName="solution-embed-card__play-button-icon"
                    />
                </div>
            </div>

            <div className="solution-embed-card__text-wrapper">
                <Button
                    text={title}
                    onClick={handleOpenDialog}
                    className="solution-embed-card__button"
                >
                    {title}
                </Button>

                <p className="solution-embed-card__read-more-label">
                    {buttonLabel || trans('containers.solutionsCarousel.watchVideo')}
                </p>
            </div>

            <Dialog
                ref={dialogRef}
                enableBackdropClose
                onClose={handleCloseDialog}
                className="solution-embed-card__dialog"
            >
                <ModalContent
                    className="solution-embed-card__dialog-content-wrapper"
                    contentClassName="solution-embed-card__dialog-content"
                >
                    {isOpen && (
                        <SafeHtml
                            html={embed.html}
                            sanitizeOptions={{
                                allowedTags: ['iframe'],
                                allowedAttributes: {
                                    iframe: ['title', 'src'],
                                },
                            }}
                            className="solution-embed-card__html-wrapper"
                        />
                    )}
                </ModalContent>
            </Dialog>
        </div>
    );
};

export default SolutionEmbedCard;
